/**
 * The creator's accounts (specification §6.2): any number of X accounts and YouTube channels per
 * wallet, linked through the platform's own authorization and unlinked here one by one. In each
 * campaign, a wallet takes part with at most five of them (§6.4). After a link the platform sends
 * the creator back here with the outcome in the address.
 *
 * Morcast measures content through its account's link, so unlinking is confirmed first, naming
 * the campaigns not yet settled where the account's content takes part: while the account is
 * unlinked, that content cannot be measured, and content that cannot be measured earns nothing.
 */

import { ArrowLeftRight, Clock, Eye, Layers, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import {
  LINK_OUTCOMES,
  type LinkedAccount,
  type LinkPlatform,
  type LinkStarter,
  PLATFORM_DETAILS,
  shownHandle,
  useStartLink,
  useUnlink,
  useWalletAccounts,
} from "../accounts/accounts.js";
import { useAllMySubmissions, type WalletSubmission } from "../campaigns/submissions.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { RequireWallet } from "../components/RequireWallet.js";
import { Badge, ErrorMessage, Loading, Notice, PageHeader, styles } from "../components/ui.js";
import { useAppConfig } from "../config.js";
import { formatUtc } from "../format.js";
import { POLICY_LINKS } from "./Privacy.js";

export function Accounts() {
  return (
    <RequireWallet reason="Sign in with the wallet you want to be paid in, then link your accounts to it.">
      <AccountsPage />
    </RequireWallet>
  );
}

const PLATFORMS: LinkPlatform[] = ["X", "YOUTUBE"];

/** What a creator should know before linking, each with its icon. */
const GOOD_TO_KNOW = [
  {
    icon: Eye,
    text: "Morcast reads your public content and organic analytics to measure your results. It can never post.",
  },
  {
    icon: Layers,
    text: "Link every account you publish with. In each campaign, you can take part with up to 5 of them, and an account belongs to one wallet at a time.",
  },
  {
    icon: Clock,
    text: "Keep an account linked until the campaigns you take part in are measured.",
  },
  {
    icon: ArrowLeftRight,
    text: "Moving to a new wallet? Sign in with it and link your accounts again. Each account moves once the campaigns it takes part in are settled. Payouts stay with the wallet that earned them.",
  },
];

/** How each platform chooses the account it links. */
const CHOOSING: Record<LinkPlatform, string> = {
  X: "X links the account you are signed in to at x.com. To link another, switch accounts on x.com first.",
  YOUTUBE: "Google asks which account, and which of its channels, to link.",
};

function AccountsPage() {
  const accounts = useWalletAccounts();
  // One starter for both platforms, so that only one link starts at a time.
  const link = useStartLink();

  return (
    <div className={styles.narrowPage}>
      <PageHeader eyebrow="Creator" title="Linked accounts">
        Link the X accounts and YouTube channels you publish with. Morcast measures the content you
        submit through them.
      </PageHeader>

      <ul className="mt-6 space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
        {GOOD_TO_KNOW.map(({ icon: Icon, text }) => (
          <li key={text} className="flex gap-2.5">
            <Icon
              aria-hidden
              className="mt-0.5 size-4 shrink-0 text-brand-600 dark:text-brand-400"
            />
            {text}
          </li>
        ))}
      </ul>

      <LinkOutcome />

      {accounts.isPending && (
        <div className="mt-8">
          <Loading />
        </div>
      )}
      {accounts.isError && <ErrorMessage error={accounts.error} />}
      {accounts.data && (
        <ul className="mt-8 grid gap-4 sm:grid-cols-2">
          {PLATFORMS.map((platform) => (
            <PlatformCard
              key={platform}
              platform={platform}
              linked={accounts.data.accounts.filter((entry) => entry.platform === platform)}
              link={link}
            />
          ))}
        </ul>
      )}

      <p className="mt-8 text-sm text-zinc-600 dark:text-zinc-400">
        The{" "}
        <Link to="/privacy" className={styles.link}>
          privacy policy
        </Link>{" "}
        explains what Morcast reads from your accounts, what it keeps and what it publishes.
      </p>
    </div>
  );
}

/** What the platform's redirect reported: a new link, or why there is none. */
function LinkOutcome() {
  const [params] = useSearchParams();
  const linked = params.get("linked");
  const error = params.get("error");
  const platform = PLATFORMS.find((entry) => PLATFORM_DETAILS[entry].slug === linked);

  if (platform) {
    return (
      <div role="status" className="mt-6">
        <Notice tone="success">Your {PLATFORM_DETAILS[platform].account} is linked.</Notice>
      </div>
    );
  }
  if (error) {
    return (
      <Notice tone="error" className="mt-6">
        {LINK_OUTCOMES[error] ?? LINK_OUTCOMES.LINK_FAILED}
      </Notice>
    );
  }
  return null;
}

/**
 * The campaigns not yet settled in which an account's content takes part, by title: those it
 * still has to be measured in.
 */
export function campaignsToMeasure(
  submissions: WalletSubmission[],
  account: Pick<LinkedAccount, "accountId">,
): string[] {
  const titles = submissions
    .filter(
      (submission) =>
        submission.status === "accepted" &&
        submission.accountId === account.accountId &&
        (submission.campaign.phase === "running" || submission.campaign.phase === "ended"),
    )
    .map((submission) => submission.campaign.title);
  return [...new Set(titles)];
}

/** One platform: its linked accounts, each with a way to unlink it, and a link to make. */
function PlatformCard({
  platform,
  linked,
  link,
}: {
  platform: LinkPlatform;
  linked: LinkedAccount[];
  link: LinkStarter;
}) {
  const { linkablePlatforms = [] } = useAppConfig();
  const unlink = useUnlink();
  const submissions = useAllMySubmissions();
  const [unlinking, setUnlinking] = useState<LinkedAccount | null>(null);
  const details = PLATFORM_DETAILS[platform];
  const error = (link.failure?.platform === platform ? link.failure.error : null) ?? unlink.error;
  // This platform's link is starting. While another platform's is, this one waits.
  const starting = link.busy === platform;
  const linkable = linkablePlatforms.includes(platform);
  const affected = unlinking ? campaignsToMeasure(submissions.data ?? [], unlinking) : [];

  return (
    <li className={`flex flex-col ${styles.card}`}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{details.name}</h2>
        {linked.length > 0 ? (
          <Badge tone="green">{`${linked.length} linked`}</Badge>
        ) : (
          <Badge tone="grey">{linkable ? "Not linked" : "Coming soon"}</Badge>
        )}
      </div>

      {linked.length > 0 ? (
        <ul
          aria-label={`Linked ${details.account}s`}
          className="mt-3 divide-y divide-zinc-200 dark:divide-white/10"
        >
          {linked.map((account) => (
            <li
              key={account.accountId}
              className="flex items-center justify-between gap-3 py-3 first:pt-1"
            >
              <div className="min-w-0">
                <p className="truncate text-sm">
                  <span className="font-medium">{shownHandle(account)}</span>{" "}
                  <span className="text-zinc-600 dark:text-zinc-400">{account.displayName}</span>
                </p>
                <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                  Linked {formatUtc(account.linkedAt)}
                </p>
              </div>
              <button
                type="button"
                aria-label={`Unlink ${shownHandle(account)}`}
                disabled={unlink.isPending}
                onClick={() => setUnlinking(account)}
                className={`shrink-0 ${styles.dangerButton}`}
              >
                Unlink
              </button>
            </li>
          ))}
        </ul>
      ) : (
        linkable && (
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
            No {details.account} is linked yet.
          </p>
        )
      )}

      {linkable ? (
        <div className="mt-auto pt-4">
          <button
            type="button"
            // The starting button keeps its colours with a spinner, and ignores clicks.
            aria-disabled={starting || undefined}
            disabled={link.busy !== null && !starting}
            onClick={() => link.start(platform)}
            className={`${linked.length > 0 ? styles.secondaryButton : styles.primaryButton} ${
              starting ? "cursor-wait" : ""
            }`}
          >
            {starting ? (
              <>
                <LoaderCircle aria-hidden className="size-4 animate-spin" />
                Opening {details.authorizer}…
              </>
            ) : linked.length > 0 ? (
              `Link another ${details.account}`
            ) : (
              `Link ${details.account}`
            )}
          </button>
          {starting ? (
            <p role="status" className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              Taking you to {details.authorizer}. Its page can take a few seconds to open.
            </p>
          ) : (
            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">{CHOOSING[platform]}</p>
          )}
          {platform === "YOUTUBE" && (
            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              Morcast uses YouTube API Services. By linking a channel you agree to the{" "}
              <a href={POLICY_LINKS.youtubeTerms} rel="noopener" className="underline">
                YouTube Terms of Service
              </a>
              .
            </p>
          )}
        </div>
      ) : (
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
          Linking {details.account}s is not available yet.
        </p>
      )}
      {error && <ErrorMessage error={error} />}
      <ConfirmDialog
        open={unlinking !== null}
        title={`Unlink ${unlinking ? shownHandle(unlinking) : details.account}?`}
        confirmLabel="Unlink"
        cancelLabel="Keep it linked"
        onConfirm={() => unlinking && unlink.mutate(unlinking)}
        onClose={() => setUnlinking(null)}
      >
        <p>
          Morcast measures your content through this link and deletes its access now. While the{" "}
          {details.account} is unlinked, its content in a campaign not yet settled cannot be
          measured, and content that cannot be measured earns nothing.
        </p>
        {affected.length > 0 ? (
          <>
            <p className="font-medium text-zinc-800 dark:text-zinc-200">
              Its content takes part in:
            </p>
            <ul className="list-disc space-y-0.5 pl-5">
              {affected.map((title) => (
                <li key={title}>{title}</li>
              ))}
            </ul>
          </>
        ) : (
          submissions.isSuccess && <p>Its content takes part in no such campaign now.</p>
        )}
        <p>You can link it again at any time.</p>
      </ConfirmDialog>
    </li>
  );
}
